/**
 * CDN 图片处理(x-image-process)
 *
 * 用于列表/卡片类图片的缩略图与质量控制,减少传输体积。
 * 注意:
 *  - 只对 CDN 图片(assets.jctrans.com / resources.jctrans.com 等)生效;
 *  - 本地静态资源(/xxx.png)不要拼接该参数;
 *  - 预览大图(灯箱、详情页)请直接使用原 URL,不要拼接该参数。
 */

/** CDN 图片处理参数前缀 */
export const IMAGE_PROCESS_KEY = 'x-image-process'

const CDN_HOST_RE =
  /(^|\.)((assets(-dev|-sit)?)|resources)\.jctrans\.com$/i

/** 全宽卡片 1x/2x/3x 分档，固定几档让 CDN 缓存命中、避免 JS 量宽换 URL */
export const MOBILE_THUMB_WIDTHS = [400, 800, 1200] as const
/** 无 srcset 时的兜底（约 2x） */
export const DEFAULT_THUMB_WIDTH = 800

export interface ThumbnailOptions {
  /** 目标宽度 */
  width?: number
  /** 目标高度(与 width 同时传时按比例缩放到该盒子里) */
  height?: number
  /** 图片质量,默认 90 */
  quality?: number
}

export interface ThumbnailSrcAttrs {
  src: string
  srcset: string
  sizes: string
}

/**
 * 是否为 CDN 图片(可拼接图片处理参数)
 */
export const isCdnImage = (url: string): boolean => {
  if (!url) return false
  try {
    const { protocol, hostname } = new URL(url.trim())
    if (protocol !== 'http:' && protocol !== 'https:') return false
    return CDN_HOST_RE.test(hostname)
  } catch {
    return false
  }
}

/**
 * 拼接缩略图处理参数
 * @param url 原始图片地址
 * @param options 尺寸与质量配置
 * @returns 处理后的地址;url 为空或非 CDN 时返回原值
 */
export const buildThumbnailUrl = (
  url: string,
  options: ThumbnailOptions = {},
): string => {
  if (!url) return ''
  if (!isCdnImage(url)) return url
  if (url.includes(IMAGE_PROCESS_KEY)) return url

  const { width, height, quality = 90 } = options

  const operations: string[] = []
  if (width || height) {
    const resizeParams = [width ? `w_${width}` : '', height ? `h_${height}` : '']
      .filter(Boolean)
      .join(',')
    operations.push(`image/resize,${resizeParams}`)
  }
  operations.push(`quality,q_${quality}`)

  const process = `${IMAGE_PROCESS_KEY}=${operations.join('/')}`
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}${process}`
}

const buildThumbnailSrcSet = (url: string, widths: readonly number[]) => {
  if (!isCdnImage(url)) return ''
  return widths
    .map((width) => `${buildThumbnailUrl(url, { width })} ${width}w`)
    .join(', ')
}

/**
 * 列表/卡片缩略图：srcset 让浏览器按 dpr 只下其中一档。
 * SSR / 客户端 HTML 一致，不量 DOM、不跟 resize 换地址。
 */
export const buildThumbnailSrcAttrs = (
  url: string,
  sizes: string,
  cssWidth?: number,
): ThumbnailSrcAttrs => {
  if (!url) return { src: '', srcset: '', sizes }

  const widths = cssWidth
    ? [cssWidth, cssWidth * 2, cssWidth * 3]
    : MOBILE_THUMB_WIDTHS
  const srcWidth = cssWidth ? cssWidth * 2 : DEFAULT_THUMB_WIDTH

  return {
    src: buildThumbnailUrl(url, { width: srcWidth }),
    srcset: buildThumbnailSrcSet(url, widths),
    sizes,
  }
}
